import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Megaphone, Plus, Pin, Trash2, Eye, Clock, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', priority: 'normal', pinned: false });
  const [page, setPage] = useState(1);

  const fetchData = async () => {
    try {
      const res = await api.get('/announcements', { params: { page, limit: 20 } });
      setAnnouncements(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [page]);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      await api.post('/announcements', form);
      toast.success('Announcement published');
      setShowCreate(false);
      setForm({ title: '', content: '', priority: 'normal', pinned: false });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try { await api.delete(`/announcements/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const markRead = async (id) => {
    try { await api.post(`/announcements/${id}/read`); fetchData(); } catch {}
  };

  const priorityColors = { urgent: 'border-l-red-500 bg-error/10', important: 'border-l-amber-500 bg-warning/10', normal: 'border-l-blue-500', info: 'border-l-stone-400' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="announcements-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Megaphone size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight dark:text-white">Announcements</h1>
              <p className="text-sm text-muted-foreground">{announcements.total} announcement{announcements.total !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-announcement-btn"><Plus size={14} className="mr-1.5" />New Announcement</Button>
        </div>

        <div className="space-y-3">
          {loading ? <div className="text-center py-12 text-muted-foreground">Loading...</div> :
           (announcements.data || []).length === 0 ? (
            <Card><CardContent className="py-12 text-center"><Megaphone size={32} className="mx-auto text-muted-foreground mb-2" /><p className="text-sm text-muted-foreground">No announcements yet.</p></CardContent></Card>
           ) : (
            announcements.data.map(ann => (
              <Card key={ann.id} className={`border-l-4 ${priorityColors[ann.priority] || ''}`} data-testid={`announcement-${ann.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {ann.pinned && <Pin size={12} className="text-warning" />}
                        <h3 className="text-sm font-semibold dark:text-white">{ann.title}</h3>
                        <Badge variant={ann.priority === 'urgent' ? 'destructive' : 'secondary'} className="text-[9px]">{ann.priority}</Badge>
                        {!ann.is_read && <Badge className="text-[9px] bg-info">New</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground dark:text-muted-foreground whitespace-pre-wrap">{ann.content}</p>
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                        <span>{ann.author_name}</span>
                        <span>{new Date(ann.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {!ann.is_read && <Button size="sm" variant="ghost" onClick={() => markRead(ann.id)} title="Mark read"><Eye size={14} /></Button>}
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(ann.id)} data-testid={`delete-ann-${ann.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
           )}
        </div>

        {announcements.total > 20 && (
          <div className="flex justify-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <span className="text-sm text-muted-foreground py-1.5">Page {page}</span>
            <Button size="sm" variant="outline" disabled={page >= announcements.pages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        )}

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent data-testid="create-announcement-dialog">
            <DialogHeader><DialogTitle>New Announcement</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} data-testid="ann-title-input" className="mt-1" /></div>
              <div><Label>Content</Label><Textarea value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))} rows={4} data-testid="ann-content-input" className="mt-1" /></div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm(f => ({ ...f, priority: v }))}>
                    <SelectTrigger className="mt-1" data-testid="ann-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="important">Important</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleCreate} className="w-full" data-testid="publish-announcement-btn">Publish Announcement</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
